import { get, post } from '../../axios/axios';
import { downloadFile, streamSse } from '../../utils/sseFetch';
import { track } from '../../analytics/track';

/** 写作类型（queryType）与界面名称 */
export const QUERY_TYPE = {
    PAPER: 'PAPER',
    COURSE_PAPER: 'COURSE_PAPER',
    OPENING_REPORT: 'OPENING_REPORT',
    LITERATURE_REVIEW: 'LITERATURE_REVIEW',
};

export const QUERY_TYPE_LABEL = {
    PAPER: '毕业长文',
    COURSE_PAPER: '课程长文',
    OPENING_REPORT: '开题报告',
    LITERATURE_REVIEW: '文献综述',
};

/** 开题报告 / 文献综述默认篇幅 */
export const SPECIAL_WORD_COUNT = 8000;
export const DEFAULT_WORD_COUNT = 15000;
export const WORD_COUNT_OPTIONS = [3000, 5000, 8000, 10000, 15000, 20000, 30000, 50000];

/** 课程长文：篇幅较短，默认 5000 字，最多 1.5 万字 */
export const COURSE_PAPER_WORD_COUNT = 5000;
export const COURSE_PAPER_WORD_COUNT_OPTIONS = [3000, 5000, 8000, 10000, 15000];

export const MODEL = { NORMAL: 'normal', ACADEMIC: 'academic' };

/** 界面元素 key → 后端 autoLightTypes / selected_extra */
export const CONTENT_TO_EXTRA = {
    image: 'DIAGRAM',
    table: 'TABLE',
    formula: 'FORMULA',
    code: 'CODE',
    data: 'DATA',
};

const unwrap = (res, fallback = '请求失败，请稍后重试') => {
    if (res && res.code === 0) return res.data;
    throw new Error(res?.msg || fallback);
};

// ---------- 埋点（统计页见 qy-lg/docs/analytics-dashboard.html；只记类型 / 模型 / 字数，不记标题与正文） ----------

const errorText = (err) => String(err?.message || err || '').slice(0, 100);

const writingProps = (payload = {}) => ({
    label: payload.queryType,
    queryType: payload.queryType,
    model: payload.model,
    wordCount: typeof payload.wordCount === 'number' ? payload.wordCount : undefined,
    refCount: Array.isArray(payload.references) ? payload.references.length : undefined,
});

/**
 * 包一层：调用前记 start，成功记 done（doneProps(result, base) 可补充返回值里的字段），失败记 fail（带错误文案）；
 * 签名、返回值与异常都原样透传，埋点本身不会抛错。
 */
const tracked = (fn, { start, done, fail, props = () => ({}), doneProps }) =>
    async (...args) => {
        const base = props(...args);
        if (start) track(start, base);
        try {
            const result = await fn(...args);
            if (done) track(done, doneProps ? doneProps(result, base) : base);
            return result;
        } catch (err) {
            if (fail) track(fail, { ...base, error: errorText(err) });
            throw err;
        }
    };

// ---------- 参考文献（沿用灵感风暴现有接口） ----------

export const searchReferences = tracked(
    async (title) =>
        unwrap(await post('/ai/ai/references/search', { majorName: title }, { slient: true }), '文献检索失败'),
    { done: 'references_searched', fail: 'references_search_failed' },
);

/** Excel / RIS 上传地址（antd Upload action 用），返回 { refId, refs } */
export const uploadRefPath = 'ai/ai/paper/upload/ref';

// ---------- 大纲 ----------

export const generateOutline = tracked(
    async (payload) => unwrap(await post('/ai/outline/gen', payload, { timeout: 300000 }), '大纲生成失败，请重试'),
    { start: 'outline_requested', done: 'outline_generated', fail: 'outline_failed', props: writingProps },
);

// ---------- 订单 ----------

/** 成功 → order_created（label = queryType，带 model / wordCount / orderNo；recreate = 预览页改配置后重新建单） */
export const createOrder = tracked(
    async (payload) => unwrap(await post('/ai/order/create', payload, { timeout: 120000 }), '创建订单失败，请重试'),
    {
        done: 'order_created',
        doneProps: (quote, base) => ({
            ...base,
            model: quote?.model || base.model,
            orderNo: quote?.orderNo,
            recreate: Boolean(base.recreate),
        }),
        fail: 'order_create_failed',
        props: (payload = {}) => ({ ...writingProps(payload), recreate: Boolean(payload.fromUid) }),
    },
);

export const getOrderInfo = async (uid) => unwrap(await get(`/ai/order/info/${uid}`));

/** 关闭付款弹窗时领取限时 88 折（5 分钟内付款有效，一单一次），返回带折扣信息的报价 */
export const claimOffer = async (uid) =>
    unwrap(await post(`/ai/order/offer/claim/${uid}`), '领取优惠失败，请重试');

export const getChapterPrices = async (uid) => unwrap(await get(`/ai/order/chapter/price/${uid}`, {}, { slient: true }));

export const getAddonConfig = async () => unwrap(await get('/ai/order/addon/config', {}, { slient: true }));

/** 预览下一节：onToken(text)；resolve 为 complete 事件（含 detailId / 标题），失败 reject */
export const previewNext = (uid, { signal, onToken } = {}) =>
    new Promise((resolve, reject) => {
        let finished = null;
        streamSse(`/ai/order/preview/${uid}`, {
            signal,
            onEvent: (data) => {
                if (data.previewStatus === 'error') {
                    finished = { error: data.message || '本节预览生成未完成，请重试' };
                    return false;
                }
                if (data.previewStatus === 'complete') {
                    finished = { ok: data };
                    return false;
                }
                if (typeof data.chatToken === 'string' && data.chatToken !== '[DONE]') {
                    onToken?.(data.chatToken);
                }
                return true;
            },
        })
            .then(() => {
                if (finished?.ok) resolve(finished.ok);
                else reject(new Error(finished?.error || '预览连接中断，请重试'));
            })
            .catch(reject);
    });

// ---------- 付款 ----------

// 创建付款单 → pay_opened（label = kind：full / chapter / upgrade，带 orderNo / price）；付款成功由支付代码派发
// window 事件 lgfb:order-paid（detail: { payNo, orderNo, kind }），track.js 记 pay_success
const payProps = (kind) => (uid) => ({ label: kind, kind, uid });
const payDoneProps = (res, base) => ({ ...base, orderNo: res?.orderNo, price: Number(res?.price) || undefined });

export const payFull = tracked(async (uid) => unwrap(await post(`/ai/order/pay/full/${uid}`)), {
    done: 'pay_opened',
    doneProps: payDoneProps,
    fail: 'pay_open_failed',
    props: payProps('full'),
});

export const payChapter = tracked(
    async (uid, chapterTitle) => unwrap(await post(`/ai/order/pay/chapter/${uid}`, {}, { params: { chapterTitle } })),
    { done: 'pay_opened', doneProps: payDoneProps, fail: 'pay_open_failed', props: payProps('chapter') },
);

export const payUpgrade = tracked(async (uid) => unwrap(await post(`/ai/order/pay/upgrade/${uid}`)), {
    done: 'pay_opened',
    doneProps: payDoneProps,
    fail: 'pay_open_failed',
    props: payProps('upgrade'),
});

// ---------- 查询 / 结果 ----------

/** 找不到返回 null（再回退老接口） */
export const queryOrder = tracked(async (no) => unwrap(await get('/ai/order/query', { no }, { slient: true })), {
    done: 'order_queried',
    doneProps: (data) => ({ label: data ? 'found' : 'missing', found: Boolean(data) }),
});

export const getDocument = async (no, doc) => unwrap(await get('/ai/order/document', { no, doc }));

export const exportDocument = (downloadUrl, title) => {
    track('document_exported');
    return downloadFile(downloadUrl, `${title || '文档'}.docx`);
};

// ---------- 本机订单记录 ----------

const ORDERS_KEY = 'lgfb.writing-orders.v1';

/** 本机订单记录变化事件：首页「我的生成记录」监听后刷新 */
export const RECENT_ORDERS_EVENT = 'lgfb:recent-orders-changed';

const notifyRecentOrders = () => {
    try {
        window.dispatchEvent(new CustomEvent(RECENT_ORDERS_EVENT));
    } catch {
        // 不支持 CustomEvent 时忽略
    }
};

/**
 * 记住一个订单（写作 / 答辩PPT），首页「我的生成记录」据此展示。
 * 同一订单（no 或 uid 相同）的记录合并更新，没传的字段保留原值；新记录排最前，最多 30 条。
 *
 * @param {object} order
 * @param {string} order.no 订单号（订单查询凭证；答辩PPT 为付款订单号或任务 uid）
 * @param {string} [order.uid] 订单 / 任务 uid
 * @param {string} [order.title]
 * @param {'WRITING'|'PPT'} [order.kind] 默认 WRITING
 * @param {string} [order.queryType] 写作类型（PAPER / OPENING_REPORT / LITERATURE_REVIEW / COURSE_PAPER）
 * @param {'UNPAID'|'PAID'} [order.status] 本机已知的付款状态（待支付 / 已付款）
 * @param {string} [order.supersedes] 被本订单替换的旧订单 uid（改附加服务重新建单时），其记录会被移除
 */
export const rememberOrder = ({ no, uid, title, kind, queryType, status, supersedes } = {}) => {
    if (!no) return;
    try {
        const prev = JSON.parse(window.localStorage.getItem(ORDERS_KEY) || '[]');
        const same = (o) => o.no === no || (uid && o.uid === uid);
        const old = prev.find(same);
        const list = prev.filter((o) => !same(o) && !(supersedes && o.uid === supersedes));
        list.unshift({
            ...old,
            no,
            uid: uid || old?.uid,
            title: title || old?.title,
            kind: kind || old?.kind || 'WRITING',
            queryType: queryType || old?.queryType,
            status: status || old?.status,
            createdAt: old?.createdAt || Date.now(),
            updatedAt: Date.now(),
        });
        window.localStorage.setItem(ORDERS_KEY, JSON.stringify(list.slice(0, 30)));
    } catch {
        // 本地存储不可用时忽略
    }
    notifyRecentOrders();
};

export const rememberedOrders = () => {
    try {
        const list = JSON.parse(window.localStorage.getItem(ORDERS_KEY) || '[]');
        return Array.isArray(list) ? list.filter((o) => o && o.no) : [];
    } catch {
        return [];
    }
};

/** 清空本机订单记录（首页「清除记录」） */
export const clearRememberedOrders = () => {
    try {
        window.localStorage.removeItem(ORDERS_KEY);
    } catch {
        // 本地存储不可用时忽略
    }
    notifyRecentOrders();
};

// ---------- 首页案例库 ----------

export const getCaseLibrary = async (category = 'all') =>
    unwrap(await get('/ai/case-library/list', { category, limit: 100 }, { slient: true }), '案例加载失败，请稍后重试');

/** 打开范文 → case_opened（label = 范文 id；category 由阅读页数据决定，这里只记 id） */
export const getCaseDocument = tracked(
    async (id) =>
        unwrap(await get(`/ai/case-library/documents/${encodeURIComponent(id)}`, {}, { slient: true }), '范文加载失败，请稍后重试'),
    { done: 'case_opened', props: (id) => ({ label: String(id ?? '').slice(0, 64), caseId: String(id ?? '').slice(0, 64) }) },
);
